import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box, Flex } from '@qga/roo-ui/components';
import pluralize from 'pluralize';
import { head, isEmpty, omit, path } from 'ramda';

import usePagination from '@shared/v2/hooks/usePagination';
import getLeadInOfferIds from '@shared/v2/utils/procuredOffer/getLeadInOfferIds';

import {
  postSearchCompareFnPrioritiseProcuredOffers,
  preSearchCompareFn,
} from '@/components/DestinationPage/utils/sortProperties/sortProperties';
import OrderableBox from '@/components/OrderableBox';
import Pagination from '@/components/Pagination';
import PropertyCard from '@/components/PropertyCard';
import QantasPointBanner from '@/components/QantasPointBanner';
import { TILE_TYPE } from '@/constants';
import useCampaignPills from '@/hooks/useCampaignPills';
import useDestination from '@/hooks/useDestination';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import {
  getCampaignCodesFromPackageOffer,
  isPackageOfferPriceForAdult as isPriceForAdult,
} from '@/utils/campaigns';
import getProcuredOfferFlightBadgeCode from '@/utils/campaigns/getProcuredOfferFlightBadgeCode';
import getCheapestOriginForProcuredOffer from '@/utils/getCheapestOriginForProcuredOffer';
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { usePointsEarn } from '@/v2/hooks/featureFlags/pointsEarn/usePointsEarn';
import useViewItemListEvent from '@/v2/hooks/useViewItemListEvent';
import useGetPackagesCheapestOffer from '../../hooks/useGetPackagesCheapestOffer';
import usePropertiesContent from '../../hooks/usePropertiesContent';
import getOfferByPropertyId from '../../utils/getOfferByPropertyId';
import PropertyOption from './components/PropertyOption';
import { PropertyOptionsHeader } from './components/PropertyOptionsHeader';

const isProcuredOffer = (property) => 'procuredOffer' in property;

const getBadgeCodeForStandardPropertyOnDestinationPage = (
  currentDestinationName,
  campaigns,
) => {
  const flightPromotionCodes = [];
  campaigns?.forEach((campaign) => {
    const campaignHasCurrentDestination =
      campaign?.activeDestinations?.includes(currentDestinationName);

    if (campaignHasCurrentDestination && campaign?.sashing?.flightPromoCode) {
      flightPromotionCodes.push(campaign.sashing.flightPromoCode);
    }
  });

  return flightPromotionCodes[0];
};

const getFlightBadgeCode = (type, currentItem, campaigns) => {
  let tileFlightPromotionCode;
  if (type === TILE_TYPE.STANDARD_PROPERTY) {
    tileFlightPromotionCode = getBadgeCodeForStandardPropertyOnDestinationPage(
      currentItem.destinationName,
      campaigns,
    );
  }

  if (type === TILE_TYPE.PROCURED_OFFER) {
    tileFlightPromotionCode = getProcuredOfferFlightBadgeCode(
      currentItem.procuredOffer.procuredOfferId,
      campaigns,
    );
  }

  return tileFlightPromotionCode;
};

const ITEMS_PER_PAGE = 20;

const PropertyOptions = ({ campaigns }) => {
  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const {
    searchConfig: { origins },
  } = destination;
  const title = destination.theme
    ? destination.title
    : `${destination.title} holidays`;
  const router = useRouter();
  const isSearchPage = router.asPath.includes('/search/');

  const { campaignPills } = useCampaignPills();
  const propertiesContent = usePropertiesContent();
  const { searchQuery, loading, packagesWithCheapestOffer } =
    useGetPackagesCheapestOffer();
  const offersByPropertyId = getOfferByPropertyId(packagesWithCheapestOffer);

  const procuredOffers = destination.procuredOffers;

  const appendPackageOfferToPropertyContent = (propertyContent) => {
    return {
      ...propertyContent,
      packageOffer: offersByPropertyId[propertyContent.id],
    };
  };

  // Merges standard offer propertyContent with procured offers
  // to avoid duplicate cards appearing
  const mapPropertyContentToProcuredOffer = (propertyContent) => {
    const offer = procuredOffers.find(
      (offer) => offer.property.id === propertyContent.id,
    );

    // Offer IDs for Procured Offer (from Sanity)
    const leadInOfferIds = getLeadInOfferIds(offer) ?? [];

    // Offer ID for cheapest offer (from PAVA)
    const propertyOfferId = propertyContent?.packageOffer?.hotelOffer?.id;

    const hasCheapestOfferIdInPO = propertyOfferId
      ? leadInOfferIds.includes(propertyOfferId)
      : true;

    return offer && hasCheapestOfferIdInPO
      ? {
          propertyContent,
          procuredOffer: offer,
          packageOffer: propertyContent.packageOffer,
          cheapestOrigin: getCheapestOriginForProcuredOffer(offer),
        }
      : propertyContent;
  };

  const enrichedPropertiesContent = propertiesContent
    .map(appendPackageOfferToPropertyContent)
    .map(mapPropertyContentToProcuredOffer);

  let orderedProperties;

  // If a search is complete, sort by total price (low-high)
  if (searchQuery && !isEmpty(searchQuery) && !loading) {
    orderedProperties = enrichedPropertiesContent
      .filter((property) => property.packageOffer)
      .sort(postSearchCompareFnPrioritiseProcuredOffers);
  }
  // If no search is active, sort POs first (by price, low-high)
  // then standard offers
  else {
    orderedProperties = [...enrichedPropertiesContent].sort(preSearchCompareFn);
  }

  // PAGINATION
  const scrollIntoPropertyOptions = useScrollIntoView('#property-options', 0);
  const { currentPageItems, goTo } = usePagination(orderedProperties, {
    pageSize: ITEMS_PER_PAGE,
  });
  const handleChangePaginationPage = (_, pageNum) => {
    if (goTo(pageNum)) {
      scrollIntoPropertyOptions();
    }
  };

  const formattedProperties = useMemo(() => {
    return orderedProperties.map((p) => ({
      ...omit('content', p),
      propertyContent: p.content,
    }));
  }, [orderedProperties]);

  const shouldAutoInvokeOnce = useMemo(() => {
    if (isSearchPage) return false;

    // If a searchQuery exists, it means this page will automatically
    // try to make a search, so we don't want to invoke this,
    // as it will send pre-search data that the user won't actually
    // be seeing.
    const hasSearchQuery =
      searchQuery?.departureDate &&
      searchQuery?.returnDate &&
      searchQuery?.originCode;
    if (hasSearchQuery) return false;

    return true;
  }, [
    isSearchPage,
    searchQuery?.departureDate,
    searchQuery?.originCode,
    searchQuery?.returnDate,
  ]);

  const isPostSearch = searchQuery && !isEmpty(searchQuery) && !loading;

  const { handleInvokeViewItemListEvent } = useViewItemListEvent({
    origins,
    properties: formattedProperties,
    title,
    destinationTitle: destination.title,
    isPostSearch,
    shouldAutoInvokeOnce,
  });

  // GA Events
  useEffect(() => {
    if (isPostSearch) {
      // When you change destinations, there is a temporary state inconsistency, where the packagesWithCheapestOffer
      // will continue to be the previous search and formattedProperties will be empty array.
      if (
        packagesWithCheapestOffer?.length > 0 &&
        formattedProperties?.length === 0
      ) {
        return;
      }

      handleInvokeViewItemListEvent();
    }
    // We don't want handleInvokeViewItemList in dependency  array
    // or this effect will be called on every re-render
    // eslint-disable-next-line
  }, [
    isSearchPage,
    isPostSearch,
    formattedProperties?.length,
    packagesWithCheapestOffer?.length,
  ]);

  const shouldShowPointsEarn = usePointsEarn();

  return (
    <>
      <PropertyOptionsHeader
        isPostSearch={isPostSearch}
        title={destination.title}
        theme={destination.theme}
        orderedPropertiesCount={orderedProperties.length}
      />
      <Flex flexDirection="column">
        {currentPageItems?.map((propertyContent, index) => {
          const packageOffer = propertyContent.packageOffer;
          const campaignCodes = getCampaignCodesFromPackageOffer(
            packageOffer,
            campaignPills,
          );
          const campaignCode = head(campaignCodes);

          const { priceForAdult, hidePriceSubText } =
            isPriceForAdult(packageOffer);

          const flightBadgeCode = getFlightBadgeCode(
            isProcuredOffer(propertyContent)
              ? TILE_TYPE.PROCURED_OFFER
              : TILE_TYPE.STANDARD_PROPERTY,
            propertyContent,
            campaigns,
          );

          const flightCampaign = path([flightBadgeCode], campaignPills);
          const flightBadgeTerms = flightCampaign?.termsConditions;
          let flightBadgeText = flightCampaign?.flightPillText;

          if (searchQuery && !isEmpty(searchQuery) && campaignCodes) {
            // if the sashing code is in one of the flight promo codes, we show that badge
            flightBadgeText =
              campaignCodes.findIndex((code) => code == flightBadgeCode) < 0
                ? undefined
                : flightBadgeText;
          }

          const promoPillText = path(
            [campaignCode],
            campaignPills,
          )?.flightPillText;

          const shouldRenderPill =
            campaignCode &&
            (flightBadgeText === undefined ||
              flightBadgeText !== promoPillText) &&
            !isProcuredOffer(propertyContent);

          return (
            <OrderableBox
              key={propertyContent.id}
              order={packageOffer ? '0' : '1'}
              mt={shouldRenderPill ? '11' : '6'}
            >
              {isProcuredOffer(propertyContent) ? (
                <PropertyCard
                  campaignPromoCode={flightBadgeCode}
                  flightBadgeText={flightBadgeText}
                  flightBadgeTerms={flightBadgeTerms}
                  hidePriceSubText={hidePriceSubText}
                  loading={loading}
                  priceForAdult={priceForAdult}
                  searchQuery={searchQuery}
                  tile={propertyContent}
                />
              ) : (
                <PropertyOption
                  loading={loading}
                  searchQuery={searchQuery}
                  propertyContent={propertyContent}
                  packageOffer={packageOffer}
                  priceForAdult={priceForAdult}
                  hidePriceSubText={hidePriceSubText}
                  flightBadgeText={flightBadgeText}
                  campaignPromoCode={flightBadgeCode}
                  index={index}
                />
              )}
              {shouldShowPointsEarn && index === 2 && (
                <Box mt={6} data-testid="QANTAS_POINTS_BANNER">
                  <QantasPointBanner
                    title="Book & Earn 10,000 Qantas Points on all Jetstar Holidays bookings*"
                    description="Simply add your Qantas Frequent Flyer number when completing a booking. Ends 30 April 2025."
                    backgroundColor="white"
                    my={0}
                  />
                </Box>
              )}
            </OrderableBox>
          );
        })}
      </Flex>
      {orderedProperties.length > 0 && (
        <Box my="7">
          <Pagination
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handleChangePaginationPage}
            totalItems={orderedProperties.length}
            paginatedItem={pluralize('package', orderedProperties.length)}
          />
        </Box>
      )}
    </>
  );
};

export default PropertyOptions;
